import { useCallback, useState } from 'react';
import { logger } from '@fiverr-private/obs';
import { raiseToast } from '@fiverr-private/toast';
import { resendEmailWithCode } from '../../../api';
import { isAxiosError } from '../../../utils/isAxiosError';
import { translateBackendError } from '../../../utils/translateBackendError';
import { ResendEmailWithCodeResponse, ResendEmailWithCodeResult } from '../../../api/resendEmailWithCode';
import { appTranslate } from '../../../i18n';
import { useResendCooldownContext } from '../../shared/ResendCooldownContext';

export const useResendWithCooldown = () => {
    const { isUnavailable, startCooldown, commitCooldown, cancelCooldown } = useResendCooldownContext();
    const [isResending, setIsResending] = useState(false);

    const resend = useCallback(
        async (email: string) => {
            if (!startCooldown()) {
                return undefined;
            }

            let result: ResendEmailWithCodeResult | undefined;

            setIsResending(true);
            try {
                result = await resendEmailWithCode(email);

                if (result.success) {
                    commitCooldown();
                } else {
                    cancelCooldown();
                }

                if (result.translatedError) {
                    raiseToast({ message: result.translatedError, type: 'error', location: 'top-end' });
                }
            } catch (error) {
                cancelCooldown();
                result = { success: false };

                if (isAxiosError(error)) {
                    const errorMessage = (error.response?.data as ResendEmailWithCodeResponse | undefined)?.error;
                    logger.error({
                        message: `Resend Activation Email Error: ${errorMessage}`,
                        package: '@fiverr-private/code_activation',
                    });
                    result.translatedError = translateBackendError(errorMessage);
                } else if (error instanceof Error) {
                    logger.error({
                        message: `Resend Activation Email Error: ${error.message}`,
                        package: '@fiverr-private/code_activation',
                    });
                    result.translatedError = appTranslate<string>('errors.general.unspecified');
                }

                if (result.translatedError) {
                    raiseToast({ message: result.translatedError, type: 'error', location: 'top-end' });
                }
            }
            setIsResending(false);

            return result;
        },
        [startCooldown, commitCooldown, cancelCooldown]
    );

    return {
        isUnavailable,
        isResending,
        resend,
    };
};
